import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { LocalDate } from "@js-joda/core";
import type { StaffProfileData } from "services/api/company/staff.service";
import { getStockholmLocalDateNow } from "web-apps/company/utils/timeAndDate";
import type { EmploymentDetailsForm } from "../../profilePanel.contract";
import type {
  DiscardChangesTarget,
  ModalState,
  TranslateFn,
} from "../profilePanel.state.contract";
import { toEmploymentDetailsForm } from "../profilePanel.state.formMappers.logic";
import {
  DraftSalaryItem,
  addSalaryDraftItem,
  deleteSalaryDraftItem,
  hasSalaryDraftHistoryChanges,
  saveSalaryDraftHistory,
  toSalaryDraftHistory,
  updateSalaryDraftValue,
} from "../profilePanel.state.salary.logic";
import { toDefaultSalaryChangeDate } from "../profilePanel.state.template.logic";

interface UseProfilePanelSalaryModalStateParams {
  companyId: number | null;
  currentEmploymentId: number | null;
  currentEmploymentStartDate: LocalDate | null;
  editedEmploymentDetails: EmploymentDetailsForm;
  effectiveFromDate: LocalDate | null;
  executeSavingAction: (action: () => Promise<void>) => void;
  isOpen: boolean;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  reloadProfile: () => void;
  setDiscardChangesTarget: Dispatch<SetStateAction<DiscardChangesTarget>>;
  setEditedEmploymentDetails: Dispatch<SetStateAction<EmploymentDetailsForm>>;
  setEffectiveFromDate: Dispatch<SetStateAction<LocalDate | null>>;
  setModalState: Dispatch<SetStateAction<ModalState>>;
  setSaveError: (value: string) => void;
  translate: TranslateFn;
  workerId: number | null;
}

interface UseProfilePanelSalaryModalStateResult {
  salaryDraftHistory: DraftSalaryItem[];
  handleOpenSalaryModal: () => void;
  handleCloseSalaryModal: () => void;
  handleConfirmSalaryModal: () => void;
  handleAddSalaryModal: () => void;
  handleDeleteSalaryModal: (salaryId: number) => void;
  handleUpdateSalaryDraftValue: (salaryId: number, value: number) => void;
}

export const useProfilePanelSalaryModalState = (
  params: UseProfilePanelSalaryModalStateParams,
): UseProfilePanelSalaryModalStateResult => {
  const [salaryDraftHistory, setSalaryDraftHistory] = useState<
    DraftSalaryItem[]
  >([]);
  const [salaryInitialHistory, setSalaryInitialHistory] = useState<
    DraftSalaryItem[]
  >([]);
  const tempDraftIdRef = useRef(-1);

  useEffect(() => {
    if (!params.isOpen) {
      setSalaryDraftHistory([]);
      setSalaryInitialHistory([]);
    }
  }, [params.isOpen, params.workerId]);

  const handleOpenSalaryModal = useCallback((): void => {
    const nextHistory = toSalaryDraftHistory(params.profile);
    params.setEditedEmploymentDetails(toEmploymentDetailsForm(params.profile));
    setSalaryDraftHistory(nextHistory);
    setSalaryInitialHistory(nextHistory);
    params.setEffectiveFromDate(
      toDefaultSalaryChangeDate(
        getStockholmLocalDateNow(),
        params.currentEmploymentStartDate,
      ),
    );
    params.setSaveError("");
    params.setModalState({ type: "editSalary" });
  }, [params]);

  const handleCloseSalaryModal = useCallback((): void => {
    if (params.isSaving) return;

    const isDirty = hasSalaryDraftHistoryChanges({
      salaryDraftHistory,
      salaryInitialHistory,
    });

    if (isDirty) {
      params.setDiscardChangesTarget("salary");
      return;
    }

    params.setModalState({ type: "none" });
  }, [params, salaryDraftHistory, salaryInitialHistory]);

  const handleConfirmSalaryModal = useCallback((): void => {
    saveSalaryDraftHistory({
      companyId: params.companyId,
      currentEmploymentId: params.currentEmploymentId,
      executeSavingAction: params.executeSavingAction,
      isSaving: params.isSaving,
      onWorkerUpdated: params.onWorkerUpdated,
      reloadProfile: params.reloadProfile,
      salaryDraftHistory,
      salaryInitialHistory,
      setModalState: (value): void => params.setModalState(value),
      setSaveError: params.setSaveError,
      translate: params.translate,
      workerId: params.workerId,
    });
  }, [params, salaryDraftHistory, salaryInitialHistory]);

  const handleAddSalaryModal = useCallback((): void => {
    const nextItem = addSalaryDraftItem({
      editedEmploymentDetails: params.editedEmploymentDetails,
      effectiveFromDate: params.effectiveFromDate,
      nextDraftId: tempDraftIdRef.current--,
      setSaveError: params.setSaveError,
      translate: params.translate,
    });
    if (nextItem == null) return;

    setSalaryDraftHistory((previousValue) => [...previousValue, nextItem]);
    params.setSaveError("");
  }, [params]);

  const handleDeleteSalaryModal = useCallback(
    (salaryId: number): void => {
      setSalaryDraftHistory((previousValue) =>
        deleteSalaryDraftItem({
          salaryDraftHistory: previousValue,
          salaryId,
        }),
      );
      params.setSaveError("");
    },
    [params],
  );

  const handleUpdateSalaryDraftValue = useCallback(
    (salaryId: number, value: number): void => {
      setSalaryDraftHistory((previousValue) =>
        updateSalaryDraftValue({
          salaryDraftHistory: previousValue,
          salaryId,
          value,
        }),
      );
      params.setSaveError("");
    },
    [params],
  );

  return {
    salaryDraftHistory,
    handleOpenSalaryModal,
    handleCloseSalaryModal,
    handleConfirmSalaryModal,
    handleAddSalaryModal,
    handleDeleteSalaryModal,
    handleUpdateSalaryDraftValue,
  };
};
